import { useRemoteThreadListRuntime } from '@assistant-ui/react';
import type { UseChatRuntimeOptions } from '@assistant-ui/react-ai-sdk';
import { type ChatTransport, createIdGenerator, type UIMessage } from 'ai';

import { useAtlasAiModel } from '#plugin-chat/hooks/useAtlasAiModel.ts';
import { useChatThreadRuntime } from '#plugin-chat/hooks/useChatThreadRuntime.ts';
import { AtlasChatTransport } from '#plugin-chat/lib/transport/AtlasChatTransport.ts';
import { createThreadListAdapter } from '#plugin-chat/lib/transport/threadListAdapter.ts';

// Wraps runtime options in a factory function for assistant-ui's dynamic thread creation
function useRuntimeHook(options: UseChatRuntimeOptions<UIMessage>) {
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional - returns hook factory for assistant-ui's runtimeHook pattern
  return () => useChatThreadRuntime(options);
}

/**
 * Creates the Atlas chat runtime with multi-threaded conversation support.
 * Configures thread list management with remote persistence and nested thread capabilities.
 */
export function useAtlasChatRuntime() {
  const atlasAiModel = useAtlasAiModel();
  // AtlasChatTransport implements ChatTransport<AtlasMessage> but useChatThreadRuntime expects
  // ChatTransport<UIMessage>. AtlasMessage extends UIMessage so the types are compatible at runtime.
  const transport = new AtlasChatTransport(atlasAiModel) as ChatTransport<UIMessage>;

  return useRemoteThreadListRuntime({
    runtimeHook: useRuntimeHook({
      transport,
      generateId,
    }),
    adapter: createThreadListAdapter(atlasAiModel),
    // Enables threads to spawn sub-threads for complex multi-turn interactions
    allowNesting: true,
  });
}

const generateId = createIdGenerator({ prefix: 'atlas-runtime' });
